import React, {useEffect, useState, useRef} from "react";
import {useMutation} from "@tanstack/react-query";
import loginUser from "../api/loginUser";
import Input from "./Input";
import InputCodeForm from "./InputCodeForm";
import Spinner from "./Spinner";
import {useAppContext} from "../context/AppContext";

type LoginCardProps = {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  onSubmitHandler: () => Promise<void>;
  loading: boolean;
  message: string;
  loginPassword: boolean;
};

const LoginCard = ({
  email,
  setEmail,
  password,
  setPassword,
  onSubmitHandler,
  loading,
  message,
  loginPassword,
}: LoginCardProps) => {
  const {setToken} = useAppContext();
  const [code, setCode] = useState(["", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const passwordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem("iris-accessToken");
    if (storedToken) {
      try {
        setToken(storedToken);
      } catch (err) {
        console.error("Invalid token:", err);
      }
    }
  }, [setToken]);

  useEffect(() => {
    if (loginPassword && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [loginPassword]);

  const loginMutation = useMutation({
    mutationFn: loginUser,
    onSuccess: () => {
      setResponseMessage("");
      setEmail("");
      setPassword("");
    },
    onError: (error: any) => {
      setResponseMessage?.(error?.message || "Login failed");
    },
  });

  const handleClick = async () => {
    try {
      setIsLoading(true);
      if (loginPassword) {
        if (!email || !password) {
          setResponseMessage("Please enter both email and password");
          return;
        }
        const data = await loginMutation.mutateAsync({email, password});
        if (data?.data?.token) {
          setToken(data.data.token);
        }
      } else {
        await onSubmitHandler();
      }
    } catch (error: any) {
      console.error(error);
      setResponseMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      {responseMessage === "Your account has not been activated yet." ? (
        <div className="card">
          <InputCodeForm code={code} setCode={setCode} email={email} />
        </div>
      ) : (
        <div className="card">
          <h2 className="title">Welcome to iRis.</h2>
          <p className="subtitle">The Amazon Connect partner in your pocket.</p>
          <form
            method="POST"
            onSubmit={(e) => {
              e.preventDefault();
              handleClick();
            }}
            className="form-login-container"
            noValidate>
            <Input
              type="email"
              placeholder=" "
              className="input"
              label="Email Address"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            {loginPassword && (
              <div style={{marginTop: "20px"}}>
                <Input
                  type="password"
                  placeholder=" "
                  className="input"
                  label="Password"
                  name="password"
                  value={password}
                  ref={passwordRef}
                  onChange={(e) => setPassword(e.target.value)}
                  maxLength={100}
                  disabled={isLoading}
                />
              </div>
            )}

            <button
              type="submit"
              style={{marginTop: "20px"}}
              className="button"
              disabled={loading || isLoading}>
              {loading || isLoading ? (
                <Spinner variant="small-spinner" alignSpin="center-spinner" />
              ) : (
                "Continue"
              )}
            </button>
          </form>

          {message && <p className="status-message">{message}</p>}
          {responseMessage && (
            <p className="status-message">{responseMessage}</p>
          )}
        </div>
      )}
      <p className="powered-by-blk">
        Powered BY:<span>iCXeed</span>
      </p>
    </React.Fragment>
  );
};

export default LoginCard;
